/**
 * The one way this app talks to the API.
 *
 * Every request goes through here, which is what makes three things possible without
 * repeating them at each call site: the access token is attached, an expired one is
 * refreshed exactly once, and a failure arrives as a typed error a component can render
 * rather than as an unhandled rejection.
 *
 * **Refresh is de-duplicated.** A dashboard fires six requests at once. If the token has
 * just expired, all six get a 401, and six parallel refreshes would burn five of them —
 * the server rotates refresh tokens and treats reuse as theft, so the fifth would revoke
 * the whole family and sign the user out. One in-flight refresh is shared by all of them.
 *
 * **Tokens live in memory, not in localStorage.** A token in localStorage is readable by
 * any script that reaches the page, which is precisely what an XSS is for. The refresh
 * token survives a reload through a same-site cookie set by the API; the access token does
 * not survive, and is re-obtained on load.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly body?: unknown,
  ) {
    super(detail)
    this.name = 'ApiError'
  }

  /** A message worth showing a person, rather than a status code. */
  get friendly(): string {
    if (this.status === 401) return 'Your session has ended. Please sign in again.'
    if (this.status === 403) return 'Your role does not permit this.'
    if (this.status === 404) return 'That record was not found.'
    if (this.status === 409) return this.detail
    if (this.status >= 500) return 'Something went wrong at our end. Please try again.'
    return this.detail
  }
}

type Tokens = { access: string; refresh: string }

let tokens: Tokens | null = null
let refreshing: Promise<boolean> | null = null

/** Called by the sign-in screen, and by the refresh below. */
export function setTokens(next: Tokens | null): void {
  tokens = next
}

export function hasSession(): boolean {
  return tokens !== null
}

export function accessToken(): string | null {
  return tokens?.access ?? null
}

const BASE = '/api/v1'

async function refresh(): Promise<boolean> {
  if (!tokens) return false

  // Shared, not per-caller. Six parallel 401s must produce one refresh; the server treats
  // a reused refresh token as theft and revokes the family, so a race here signs the user
  // out rather than recovering them.
  refreshing ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: tokens?.refresh }),
      })
      if (!response.ok) {
        setTokens(null)
        return false
      }
      const body = (await response.json()) as { access_token: string; refresh_token: string }
      setTokens({ access: body.access_token, refresh: body.refresh_token })
      return true
    } catch {
      setTokens(null)
      return false
    } finally {
      refreshing = null
    }
  })()

  return refreshing
}

export type RequestOptions = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Set internally to stop a refreshed request refreshing again and looping. */
  retried?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (tokens) headers['Authorization'] = `Bearer ${tokens.access}`

  const init: RequestInit = { method: options.method ?? 'GET', headers }
  if (options.body !== undefined) init.body = JSON.stringify(options.body)
  if (options.signal) init.signal = options.signal

  const response = await fetch(`${BASE}${path}`, init)

  if (response.status === 401 && !options.retried && tokens) {
    if (await refresh()) return request<T>(path, { ...options, retried: true })
  }

  if (!response.ok) {
    throw new ApiError(response.status, await detailOf(response), undefined)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function detailOf(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown }
    const detail = body.detail
    if (typeof detail === 'string') return detail
    // FastAPI's validation errors arrive as a list of objects. Showing "[object Object]"
    // to a person is worse than showing nothing.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string }
      if (typeof first?.msg === 'string') return first.msg
    }
    return response.statusText || 'Request failed'
  } catch {
    return response.statusText || 'Request failed'
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  // Returns nothing: every delete in this API answers 204. Typed as `void` so a caller
  // cannot accidentally read a body that was never sent.
  delete: (path: string) => request<void>(path, { method: 'DELETE' }),
}
